import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Button } from '@agconnections/grow-ui';
import { useFormikContext } from 'formik';
import usePDFTools from 'hooks/usePDFTools';
import SavePDFIcon from 'components/Icons/components/SavePDFIcon';
import { Context } from 'components/Store';
import { IntegrationsContext } from 'screens/Integrations/context/IntegrationsProvider';
import { MccainContext } from 'screens/Integrations/MccainIntegration/context/Provider';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { useHistory } from 'react-router-dom';
import { INTEGRATIONS } from 'screens/Integrations/helpers/constants';
import { SubmissionSummaryContext } from 'screens/Integrations/SubmissionSummary/context/SubmissionSummaryProvider';
import useSubmissionAnalytics from 'screens/Integrations/hooks/useSubmissionAnalytics';
import featureFlagRenderItem from 'helpers/featureFlagRenderItem';

const options = {
  filename: `McCain_Export_${new Date()
    .toJSON()
    .slice(0, 10)
    .replaceAll('-', '_')}`,
  jsPDF: { unit: 'in', format: 'a4', orientation: 'landscape' }
};

const mccainFooterIntegrationDefaultPropOnNext = () => {};
const mccainFooterIntegrationDefaultPropOnBack = () => {};
const mccainFooterIntegrationDefaultPropOnNextValidation = () => true;
const MccainFooterIntegration = ({
  MCCAIN_PDF_WRAPPER = '',
  nextButtonDisabled = true,
  onNextValidation = mccainFooterIntegrationDefaultPropOnNextValidation,
  onNext = mccainFooterIntegrationDefaultPropOnNext,
  onBack = mccainFooterIntegrationDefaultPropOnBack
}) => {
  const { events, triggerAnalyticsEvent } = useSubmissionAnalytics();
  const [shouldStart, setShouldStart] = useState(false);
  const [{ organization }] = useContext(Context);

  const {
    isLoading,
    startSubmission,
    updateSubmissionFilterCropZoneIds,
    updateCropSeasons
  } = useContext(SubmissionSummaryContext);

  const {
    releaseBeApplicationsEnable,
    releaseBeApplicationsByOrg
  } = useFlags();

  const feApplicationsActive = featureFlagRenderItem(
    releaseBeApplicationsEnable,
    releaseBeApplicationsByOrg,
    organization?.id
  );

  const { values } = useFormikContext();
  const history = useHistory();

  const {
    goForward,
    goBack,
    setModalSubmissionState,
    state: { step }
  } = useContext(MccainContext);
  const {
    state: { selectedProperties }
  } = useContext(IntegrationsContext);

  const { mccainDownloadPDF } = usePDFTools();

  const handleNext = async () => {
    if (onNextValidation()) {
      if (feApplicationsActive && step === 3) {
        updateSubmissionFilterCropZoneIds(
          selectedProperties?.map(({ properties }) => properties.id) || []
        );
        updateCropSeasons(values.selectedCropSeasons);
        setShouldStart(true);
        return;
      }
      onNext();
      goForward();
    }
  };

  useEffect(() => {
    if (!shouldStart) {
      return;
    }
    startSubmission().then(() => {
      setShouldStart(false);
      onNext();
      goForward();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldStart]);

  const handleBack = () => {
    onBack();
    goBack();
  };

  const handleCancel = () => {
    triggerAnalyticsEvent(events.epic.Integrations.submissionCanceled, {
      vendor: INTEGRATIONS.mccain
    });
    history.goBack();
  };

  const renderFooter = () => {
    switch (step) {
      case 2:
        return (
          <div
            className="flex flex-row border-t-2 justify-between pt-4 "
            data-testid="mccain-crop-season-footer"
            id="mccain-footer"
          >
            <Button type="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <div className="flex flex-row gap-6">
              <Button
                type="outline"
                onClick={handleBack}
                icon={
                  <FontAwesomeIcon fixedWidth size="1x" icon={faChevronLeft} />
                }
              >
                Back
              </Button>
              <Button
                type="primary"
                onClick={() => {
                  triggerAnalyticsEvent(
                    events.epic.Integrations.submissionSeasonSelected,
                    { vendor: INTEGRATIONS.mccain }
                  );
                  handleNext();
                }}
                ghost
                disabled={
                  !values?.selectedCropSeasons?.length > 0 ||
                  !values.cropSeasonStartDate ||
                  !values.cropSeasonEndDate
                }
              >
                Next: Select Properties
              </Button>
            </div>
          </div>
        );
      case 3:
        return (
          <div
            className="flex flex-row border-t-2 bg-white w-auto justify-between px-4 py-6 h-auto"
            data-testid="mccain-properties-footer"
            id="mccain-footer"
          >
            <Button type="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <div className="flex flex-row gap-6">
              <Button
                type="outline"
                disabled={isLoading}
                onClick={handleBack}
                icon={
                  <FontAwesomeIcon fixedWidth size="1x" icon={faChevronLeft} />
                }
              >
                Back
              </Button>
              <Button
                type="primary"
                ghost
                loading={isLoading}
                onClick={() => {
                  triggerAnalyticsEvent(
                    events.epic.Integrations.submissionPropertiesSelected,
                    { vendor: INTEGRATIONS.mccain }
                  );
                  handleNext();
                }}
                disabled={nextButtonDisabled || isLoading}
              >
                Next: Review & Submit
              </Button>
            </div>
          </div>
        );
      case 4:
        return (
          <div
            className="w-full h-24 bg-black"
            data-testid="mccain-review-footer"
            id="mccain-footer"
            data-html2canvas-ignore
          >
            <div className="flex flex-row border-t-2 bg-white w-auto justify-between px-4 py-6 h-auto">
              <Button type="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <div className="flex flex-row gap-6">
                <Button
                  type="outline"
                  onClick={handleBack}
                  icon={
                    <FontAwesomeIcon
                      fixedWidth
                      size="1x"
                      icon={faChevronLeft}
                    />
                  }
                >
                  Back
                </Button>
                <Button
                  id="save-button"
                  type="primary"
                  ghost
                  icon={<SavePDFIcon />}
                  onClick={() => {
                    mccainDownloadPDF(MCCAIN_PDF_WRAPPER, options);
                  }}
                >
                  Save as PDF
                </Button>
                <Button
                  type="primary"
                  onClick={() => {
                    setModalSubmissionState(true);
                  }}
                >
                  Submit to McCain
                </Button>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return renderFooter();
};

MccainFooterIntegration.propTypes = {
  nextButtonDisabled: PropTypes.bool,
  onNext: PropTypes.func,
  onBack: PropTypes.func,
  MCCAIN_PDF_WRAPPER: PropTypes.string,
  onNextValidation: PropTypes.func
};

export default MccainFooterIntegration;
